/**
*
* useUpdateTopics
*
*/

import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import config from 'config/index.js';
import { idValues, idList } from 'utils/arrays.js';
import useAuthorizedRequest from 'containers/Api2/useAuthorizedRequest.js';
import { PENDING_STATUS } from 'components/Post/constants.js';
import { reportLog } from 'utils/sentry.js';
import { mergeLists } from 'utils/useApi3/reduceObjects.js';
import { PAGESIZE } from './constants.jsx';

function mergeTopic(topic, updatedTopic) {
  return topic ? { ...topic, ...updatedTopic } : updatedTopic;
}

function updater(state, topics) {
  if (!state.list) {
    reportLog('No list in updateTopics', state);
    return state;
  }
  if (topics.error || topics.list.length === 0) {
    return state;
  }
  const result = { ...state };
  const pending = { ...state.pending };
  const lists = { pending: [], released: [] };
  const removedPending = [];
  idValues(topics).forEach((topic) => {
    const { id, status } = topic;
    if (PENDING_STATUS.includes(status)) {
      pending[id] = mergeTopic(pending[id], topic);
      lists.pending.push(id);
    } else {
      result[id] = mergeTopic(result[id], topic);
      lists.released.unshift(id); // Ensure latest first
      if (lists.pending.includes(id)) {
        delete pending[id];
        removedPending.push(id);
      }
      if (topic.subTopics) {
        result[id].subTopicIds = idList(topic.subTopics);
        result[id].subTopicIds.forEach((sid) => {
          result[sid] = mergeTopic(result[sid], topic.subTopics[sid]);
        });
      }
    }
  });
  result.list = mergeLists(lists.released, state.list);
  pending.list = mergeLists(lists.pending, pending.list).filter((id) => !removedPending.includes(id));
  return { ...result, pending };
}

function useUpdateTopics(props) {
  const { baseUrl, topics, setTopics } = props;
  const { authFetch } = useAuthorizedRequest();
  const { archive } = useParams();
  const ignore = config.env === 'production' ? [401] : undefined;
  const timestamp = topics.nextSince || topics.timeStamp;

  const updateTopics = useCallback(async () => {
    const archived = archive ? '&archived=true' : '';
    if (!timestamp) {
      return;
    }
    const updatedTopics = await authFetch({ url: `${baseUrl}?since=${timestamp}&limit=${PAGESIZE}${archived}`, ignore, noRetry: true, reload: true });
    setTopics((prev) => updater(prev, updatedTopics));
  }, [baseUrl, timestamp, setTopics, archive]);

  return updateTopics;
}

export default useUpdateTopics;
